import { useRef } from "react";
import type { ExpectedDebitsTable } from "../../types";
import { RELATION_CLASSES } from "../../types";
import { Button } from "../Button";
import { ModalShell } from "../ModalShell";
import { IhqTypePill } from "../Pills";
import { RELATION_CLASS_LABELS } from "../../utils/labels";
import { compositeOfBands } from "../../utils/expectedDebitsBands";

interface ExpectedDebitsTableDrawerProps {
  table: ExpectedDebitsTable;
  onClose: () => void;
  children?: never;
}

/** Read-only detail view of a saved table. */
export function ExpectedDebitsTableDrawer({ table, onClose }: ExpectedDebitsTableDrawerProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  return (
    <ModalShell
      title={table.name}
      description={table.notes ?? undefined}
      onClose={onClose}
      initialFocusRef={closeRef}
      size="lg"
      footer={
        <Button ref={closeRef} variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="mb-4 flex items-center gap-3 text-sm text-gray-600">
        <IhqTypePill ihqType={table.ihqType} />
        <span>{table.isActive ? "Active" : "Inactive"}</span>
        <span>Created by {table.createdByName}</span>
      </div>
      {RELATION_CLASSES.map((relationClass) => (
        <section key={relationClass} className="mb-5" aria-label={RELATION_CLASS_LABELS[relationClass]}>
          <h3 className="mb-2 text-sm font-semibold text-brand-navy">{RELATION_CLASS_LABELS[relationClass]}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                <tr className="border-b border-gray-200">
                  <th scope="col" className="px-2 py-1">Min Age</th>
                  <th scope="col" className="px-2 py-1">Max Age</th>
                  <th scope="col" className="px-2 py-1 text-right">Male Exp</th>
                  <th scope="col" className="px-2 py-1 text-right">Male Base</th>
                  <th scope="col" className="px-2 py-1 text-right">Female Exp</th>
                  <th scope="col" className="px-2 py-1 text-right">Female Base</th>
                  <th scope="col" className="px-2 py-1 text-right">Comp Exp</th>
                  <th scope="col" className="px-2 py-1 text-right">Comp Base</th>
                </tr>
              </thead>
              <tbody>
                {table.bands
                  .filter((band) => band.relationClass === relationClass)
                  .sort((a, b) => a.ageStart - b.ageStart)
                  .map((band) => (
                    <tr key={band.id} className="border-b border-gray-100 tabular-nums">
                      <td className="px-2 py-1">{band.ageStart}</td>
                      <td className="px-2 py-1">{band.ageEnd}</td>
                      <td className="px-2 py-1 text-right">{band.maleExpected}</td>
                      <td className="px-2 py-1 text-right">{band.maleBase}</td>
                      <td className="px-2 py-1 text-right">{band.femaleExpected}</td>
                      <td className="px-2 py-1 text-right">{band.femaleBase}</td>
                      <td className="px-2 py-1 text-right">{band.compositeExpected}</td>
                      <td className="px-2 py-1 text-right">{band.compositeBase}</td>
                    </tr>
                  ))}
              </tbody>
              <tfoot className="border-t border-gray-200 bg-gray-50 text-xs">
                <tr>
                  <th scope="row" colSpan={2} className="px-2 py-1.5 text-left font-semibold text-gray-600" title="Simple average of the bands above; the exposure-weighted formula is still to be confirmed.">
                    Composite {RELATION_CLASS_LABELS[relationClass]}
                  </th>
                  {(["maleExpected", "maleBase", "femaleExpected", "femaleBase", "compositeExpected", "compositeBase"] as const).map((key) => {
                    const value = compositeOfBands(table.bands.filter((band) => band.relationClass === relationClass))[key];
                    return (
                      <td key={key} className="px-2 py-1.5 text-right font-medium tabular-nums">
                        {value === null ? "–" : value.toFixed(2)}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      ))}
    </ModalShell>
  );
}
